import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["modal", "dateInput", "hourInput", "minuteInput", "error"]

  connect() {
    this._submitted = false;
    if (this.hasModalTarget) {
      this._disableModalInputs();
      if ('hasSchedulingErrors' in this.modalTarget.dataset) {
        this.openModal();
      }
    }
    // Register before form-errors connects (announcements-publish-later is first
    // in data-controller) so this fires before onBeforeUnloadTurbo.
    document.addEventListener("turbolinks:before-visit", this._onBeforeVisit);
    this.element.addEventListener("submit", this._onFormSubmit);
    this.element.addEventListener("ajax:success", this._onAjaxSuccess);
  }

  openModal() {
    const formErrorsController = this.application
      .getControllerForElementAndIdentifier(this.element, 'form-errors');

    if (formErrorsController && !this._validateRequiredFields(formErrorsController)) {
      return;
    }

    this._enableModalInputs();
    this.clearError();
    this.modalTarget.classList.remove("hidden");
  }

  disconnect() {
    document.removeEventListener("turbolinks:before-visit", this._onBeforeVisit);
    this.element.removeEventListener("submit", this._onFormSubmit);
    this.element.removeEventListener("ajax:success", this._onAjaxSuccess);
  }

  closeModal() {
    this._disableModalInputs();
    this.modalTarget.classList.add("hidden");
  }

  _validateRequiredFields(formErrorsController) {
    const modal = this.hasModalTarget ? this.modalTarget : null;
    const fields = Array.from(this.element.elements).filter(
      field => field.required && (!modal || !modal.contains(field))
    );

    let isValid = true;
    fields.forEach(field => {
      if (!formErrorsController.validateField(field)) isValid = false;
    });
    return isValid;
  }

  _disableModalInputs() {
    if (this.hasDateInputTarget) this.dateInputTarget.disabled = true;
    if (this.hasHourInputTarget) this.hourInputTarget.disabled = true;
    if (this.hasMinuteInputTarget) this.minuteInputTarget.disabled = true;
  }

  _enableModalInputs() {
    if (this.hasDateInputTarget) this.dateInputTarget.disabled = false;
    if (this.hasHourInputTarget) this.hourInputTarget.disabled = false;
    if (this.hasMinuteInputTarget) this.minuteInputTarget.disabled = false;
  }

  _onBeforeVisit = () => {
    if (!this._submitted) return;
    this._clearFormDirtyState();
  }

  _onAjaxSuccess = () => {
    this._submitted = false;
    this._clearFormDirtyState();
  }

  _onFormSubmit = (event) => {
    if (event.submitter?.value !== 'scheduled') return;

    if (!this._isValidSchedule()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError();
    } else {
      this.clearError();
      this._submitted = true;
      this._clearFormDirtyState();
    }
  }

  _clearFormDirtyState() {
    const formErrorsController = this.application
      .getControllerForElementAndIdentifier(this.element, 'form-errors');
    if (formErrorsController) formErrorsController.inputsChanged.clear();
  }

  showError() {
    this.errorTarget.classList.remove("hidden");
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden");
    }
  }

  _isValidSchedule() {
    const date = this.dateInputTarget.value;
    const hour = this.hourInputTarget.value;
    const minute = this.minuteInputTarget.value;

    if (!date || hour === '' || minute === '') return false;

    const scheduled = new Date(
      `${date}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`
    );

    return scheduled > new Date();
  }
}
